/**
 * @file: primitives.tsx
 *
 * Tiny shared layout components for the per-type message bodies. None
 * of these is meant to be reused outside the message-body family; they
 * exist so the per-type files don't repeat the same Typography +
 * rpx wrapping for title / body / label / chip patterns.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FC, ReactNode } from 'react';
import { View } from 'react-native';

export const labelColor = '#8e8e93';
export const valueColor = '#1f2937';

export const TitleText: FC<{ children: ReactNode; color?: string }> = ({
  children,
  color,
}) => {
  const { rpx } = useResponsiveContext();
  return (
    <Typography.Text size={rpx(15)} color={color ?? '#111827'} bold>
      {children}
    </Typography.Text>
  );
};

export const BodyText: FC<{
  children: ReactNode;
  color?: string;
  /** Unscaled spacing (rpx multiplier). Scaled inside so call sites stay terse. */
  mt?: number;
}> = ({ children, color, mt }) => {
  const { rpx } = useResponsiveContext();
  return (
    <Typography.Text
      size={rpx(13)}
      color={color ?? '#4b5563'}
      lineHeight={rpx(19)}
      mt={mt != null ? rpx(mt) : undefined}
    >
      {children}
    </Typography.Text>
  );
};

export const LabelRow: FC<{
  label: string;
  value?: string | number | null;
}> = ({ label, value }) => {
  const { rpx } = useResponsiveContext();
  if (value == null || value === '') return null;
  return (
    <View
      css={`
        flex-direction: row;
        margin-top: ${rpx(4)}px;
      `}
    >
      <Typography.Text
        size={rpx(12)}
        color={labelColor}
        style={{ minWidth: rpx(56) }}
      >
        {label}
      </Typography.Text>
      <Typography.Text size={rpx(12)} color={valueColor} style={{ flex: 1 }}>
        {String(value)}
      </Typography.Text>
    </View>
  );
};

export const Chip: FC<{ text: string; color: string; bg?: string }> = ({
  text,
  color,
  bg,
}) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      css={`
        background-color: ${bg ?? `${color}1f`};
        padding: ${rpx(2)}px ${rpx(8)}px;
        border-radius: ${rpx(10)}px;
        align-self: flex-start;
      `}
    >
      <Typography.Text size={rpx(11)} color={color} medium>
        {text}
      </Typography.Text>
    </View>
  );
};

export const KeyValueGrid: FC<{
  items: Array<[string, string | null | undefined]>;
}> = ({ items }) => {
  const { rpx } = useResponsiveContext();
  const visible = items.filter(([, v]) => v != null && v !== '');
  if (visible.length === 0) return null;
  return (
    <View
      css={`
        flex-direction: row;
        flex-wrap: wrap;
        margin-top: ${rpx(8)}px;
        margin-horizontal: -${rpx(4)}px;
      `}
    >
      {visible.map(([label, value], idx) => (
        <View
          key={`${label}:${idx}`}
          css={`
            width: 50%;
            padding: ${rpx(4)}px;
          `}
        >
          <Typography.Text size={rpx(11)} color={labelColor}>
            {label}
          </Typography.Text>
          <Typography.Text
            size={rpx(13)}
            color={valueColor}
            mt={rpx(2)}
            medium
            numberOfLines={1}
          >
            {value}
          </Typography.Text>
        </View>
      ))}
    </View>
  );
};
